import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Space } from 'antd';
import useIsMobile from '../../../hooks/useIsMobile';
import { endGridSymbolFromBroker } from '../../../services/gridImportApi';
import { ImportJobView, importInProgressOf } from '../../../services/importJobApi';
import ImportJobProgress from '../../../components/ImportJobProgress';
import { formatInt } from '../../../utils/numberFormat';

export const END_PHRASE = 'END';

export interface GridEndFromBrokerModalProps {
  open: boolean;
  onClose: () => void;
  dataConfigId: string | null;
  symbol: string | null;
  heldQty: number;
  /** Fires once the job is READY: the symbol has left the grid. */
  onEnded: (job: ImportJobView) => void;
}

const GREY = '#848E9C';

/** Takes one symbol out of the grid without selling: typed confirmation, then the job's progress. */
const GridEndFromBrokerModal: React.FC<GridEndFromBrokerModalProps> = ({
  open,
  onClose,
  dataConfigId,
  symbol,
  heldQty,
  onEnded,
}) => {
  const isMobile = useIsMobile();
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<{ id: string; note: string | null } | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) return;
    setConfirmText('');
    setError(null);
    setJob(null);
    setDone(false);
  }, [open]);

  const submit = async () => {
    if (!dataConfigId || !symbol) return;
    setSubmitting(true);
    setError(null);
    try {
      const { importId } = await endGridSymbolFromBroker(dataConfigId, symbol, confirmText.trim());
      setJob({ id: importId, note: null });
    } catch (e: any) {
      const open409 = importInProgressOf(e);
      if (open409) {
        setJob({ id: open409.importId, note: open409.message });
        return;
      }
      setError(e?.response?.data?.message ?? e?.message ?? 'The symbol could not be ended.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`End ${symbol ?? ''} from broker`}
      footer={null}
      width={isMobile ? 'calc(100vw - 16px)' : 520}
      style={isMobile ? { top: 8, maxWidth: 'calc(100vw - 16px)' } : undefined}
      destroyOnClose
    >
      {job ? (
        <div style={{ fontSize: 12, color: '#EAECEF', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {job.note && <Alert type="warning" showIcon style={{ fontSize: 11 }} message={job.note} />}
          <span style={{ color: GREY }}>
            Ending in the background: the database and the bot change together, or neither does. Closing this
            window does not stop it.
          </span>
          <ImportJobProgress
            importId={job.id}
            onReady={(view) => {
              setDone(true);
              onEnded(view);
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type={done ? 'primary' : 'default'} onClick={onClose}>{done ? 'Done' : 'Close'}</Button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#EAECEF', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Alert
            type="warning"
            showIcon
            style={{ fontSize: 11 }}
            message={`${symbol} leaves this grid without selling anything`}
            description={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li>
                  {`Its ${formatInt(heldQty)} shares go back to the account's unclaimed holdings (basic book).`}
                </li>
                <li>Its allocation is released.</li>
                <li>To trade it again in a grid, use Import Holdings.</li>
                <li>The grid bot must be stopped.</li>
              </ul>
            }
          />
          <div>
            <div style={{ color: GREY, marginBottom: 4 }}>{`Type ${END_PHRASE} to confirm`}</div>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={END_PHRASE}
              style={{ width: isMobile ? '100%' : 220 }}
            />
          </div>
          {error && <Alert type="error" showIcon message={error} />}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space wrap style={{ justifyContent: 'flex-end' }}>
              <Button onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                danger
                type="primary"
                loading={submitting}
                disabled={confirmText.trim() !== END_PHRASE || !dataConfigId || !symbol}
                onClick={() => void submit()}
              >
                End from broker
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default GridEndFromBrokerModal;
